import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

class Upload {
  constructor(file, url, callback, onprogress) {
    this.onprogress = onprogress
    this.file = file
    const upload = new DirectUpload(file, url, this)
    upload.create((error, blob) => {
      if (error) {
        console.error('Upload error:', error)
        callback(null, error)
      } else {
        callback(blob.signed_id)
        this.onprogress(100)
      }
    })
  }

  directUploadWillStoreFileWithXHR(request) {
    request.upload.addEventListener("progress", event => this.directUploadDidProgress(event))
  }

  directUploadDidProgress(event) {
    const percent = event.loaded * 100 / event.total
    this.onprogress(Math.min(99, percent))
  }
}

export default class extends Controller {
  static targets = ["input", "messages", "inputArea", "fileInput", "filePreview", "dropZone", "dropOverlay"]
  static values = {
    skipAutoReset: { type: Boolean, default: false },
    directUploadUrl: { type: String, default: "/rails/active_storage/direct_uploads" },
    acceptedFiles: { type: String, default: "image/*,application/pdf,video/*" }
  }

  connect() {
    this.scrollToBottom()
    this.observeMessages()
    this.saveOriginalFormState()
    this.pendingFiles = []
    this.setupDropZone()

    // Listen for autosave events to reset chat (edits need new conversation to take effect)
    // Skip this behavior when skipAutoReset is true (e.g., classification testing)
    if (!this.skipAutoResetValue) {
      this.boundOnAutosave = this.onAutosave.bind(this)
      document.addEventListener("autosave:saved", this.boundOnAutosave)
    }
  }

  saveOriginalFormState() {
    if (!this.hasInputAreaTarget) return

    const form = this.inputAreaTarget.querySelector('form')
    if (!form) return

    this.originalFormAction = form.getAttribute('action')
    this.originalHiddenFields = []

    // Save all hidden fields except csrf token and conversation_id
    form.querySelectorAll('input[type="hidden"]').forEach(field => {
      if (field.name !== 'authenticity_token' && field.name !== 'conversation_id') {
        this.originalHiddenFields.push({ name: field.name, value: field.value })
      }
    })
  }

  disconnect() {
    if (this.boundOnAutosave) {
      document.removeEventListener("autosave:saved", this.boundOnAutosave)
    }
  }

  onAutosave() {
    // Only reset if there's an active conversation (has messages)
    const hasMessages = this.hasMessagesTarget &&
      this.messagesTarget.querySelector('.chat-message') !== null
    if (hasMessages) {
      this.newChat({ skipFocus: true })
    }
  }

  clearInput() {
    if (this.hasInputTarget) {
      this.inputTarget.value = ""
      this.inputTarget.focus()
    }
    this.clearPendingFiles()
  }

  // Intercept form submission to add file signed IDs
  submitForm(event) {
    if (this.hasFilesUploading()) {
      event.preventDefault()
      return
    }

    const form = event.target
    const fileIds = this.getUploadedFileIds()

    // Remove any existing file inputs
    form.querySelectorAll('input[name="files[]"]').forEach(el => el.remove())

    // Add hidden fields for each uploaded file
    fileIds.forEach(signedId => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = 'files[]'
      input.value = signedId
      form.appendChild(input)
    })
  }

  newChat(options = {}) {
    const { skipFocus = false } = options

    // Clear all messages and show empty state
    if (this.hasMessagesTarget) {
      this.messagesTarget.innerHTML = `
        <div id="chat-empty-state" class="sandbox-empty-state">
          <div class="sandbox-empty-icon">
            <img src="/assets/sidebar/ai.svg" class="w-8 h-8 opacity-40" />
          </div>
          <p>Send a message to test your AI agent</p>
        </div>
      `
    }

    // Clear pending files
    this.clearPendingFiles()

    // Restore the input form (in case it was replaced with ended state)
    if (this.hasInputAreaTarget) {
      const form = this.inputAreaTarget.querySelector('form')
      if (form) {
        // Remove conversation_id hidden field if it exists
        const conversationIdField = form.querySelector('input[name="conversation_id"]')
        if (conversationIdField) {
          conversationIdField.remove()
        }
        // Clear input and optionally focus (content is a textarea in the standardized composer)
        const input = form.querySelector('[name="content"]')
        if (input) {
          input.value = ""
          if (!skipFocus) input.focus()
        }
      } else {
        // Form was replaced (ended state), restore it
        // Reset the container classes first
        this.inputAreaTarget.className = ""
        this.inputAreaTarget.setAttribute("data-sandbox-chat-target", "inputArea")

        const formAction = this.originalFormAction || '/ai/sandbox_messages'
        const hiddenFieldsHtml = (this.originalHiddenFields || [])
          .map(f => `<input type="hidden" name="${f.name}" value="${f.value}" />`)
          .join('')

        this.inputAreaTarget.innerHTML = `
          <form class="composer" action="${formAction}" accept-charset="UTF-8" method="post" data-controller="emoji-picker" data-action="submit->sandbox-chat#submitForm turbo:submit-end->sandbox-chat#clearInput">
            <input type="hidden" name="authenticity_token" value="${document.querySelector('meta[name="csrf-token"]')?.content || ''}" autocomplete="off" />
            ${hiddenFieldsHtml}
            <textarea name="content" rows="1" placeholder="Type a message or drop files..." autocomplete="off" class="composer-input text-sm" data-sandbox-chat-target="input" data-controller="composer-input" data-action="input->composer-input#resize keydown->composer-input#keydown"></textarea>
            <div class="sandbox-file-preview" data-sandbox-chat-target="filePreview"></div>
            <div class="composer-toolbar">
              <div class="composer-actions">
                <button type="button" class="composer-attach" data-action="click->sandbox-chat#triggerFileInput" title="Attach files">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13" />
                  </svg>
                </button>
                <input type="file" multiple accept="image/*,application/pdf,video/*" class="hidden" data-sandbox-chat-target="fileInput" data-action="change->sandbox-chat#handleFileSelect" />
                <button type="button" class="composer-emoji" data-action="emoji-picker#toggle" data-emoji-picker-target="button" title="Add emoji" aria-label="Add emoji">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M15.182 15.182a4.5 4.5 0 0 1-6.364 0M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0ZM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Z" />
                  </svg>
                </button>
              </div>
              <input type="submit" value="Send" class="btn-primary-xs" />
            </div>
          </form>
        `

        // Optionally focus the new input after a tick to allow Stimulus to register it
        if (!skipFocus) {
          requestAnimationFrame(() => {
            const input = this.inputAreaTarget.querySelector('[name="content"]')
            if (input) input.focus()
          })
        }
      }
    }
  }

  scrollToBottom() {
    if (this.hasMessagesTarget) {
      this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
    }
  }

  observeMessages() {
    if (!this.hasMessagesTarget) return

    const observer = new MutationObserver(() => {
      this.scrollToBottom()
    })

    observer.observe(this.messagesTarget, {
      childList: true,
      subtree: true
    })
  }

  // File upload methods
  setupDropZone() {
    if (!this.hasDropZoneTarget) return

    this.dragCounter = 0

    this.dropZoneTarget.addEventListener('dragenter', (e) => {
      e.preventDefault()
      e.stopPropagation()
      this.dragCounter++
      this.showDropOverlay()
    })

    this.dropZoneTarget.addEventListener('dragover', (e) => {
      e.preventDefault()
      e.stopPropagation()
    })

    this.dropZoneTarget.addEventListener('dragleave', (e) => {
      e.preventDefault()
      e.stopPropagation()
      this.dragCounter--
      if (this.dragCounter === 0) {
        this.hideDropOverlay()
      }
    })

    this.dropZoneTarget.addEventListener('drop', (e) => {
      e.preventDefault()
      e.stopPropagation()
      this.dragCounter = 0
      this.hideDropOverlay()
      const files = Array.from(e.dataTransfer.files)
      files.forEach(file => this.addFile(file))
    })
  }

  showDropOverlay() {
    if (this.hasDropOverlayTarget) {
      this.dropOverlayTarget.classList.add('active')
    }
  }

  hideDropOverlay() {
    if (this.hasDropOverlayTarget) {
      this.dropOverlayTarget.classList.remove('active')
    }
  }

  triggerFileInput() {
    if (this.hasFileInputTarget) {
      this.fileInputTarget.click()
    }
  }

  handleFileSelect(event) {
    const files = Array.from(event.target.files)
    files.forEach(file => this.addFile(file))
    event.target.value = '' // Reset input so same file can be selected again
  }

  addFile(file) {
    if (!this.matchMimeType(file.type)) {
      console.warn(`File type ${file.type} not accepted`)
      return
    }

    const fileId = `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const fileData = { id: fileId, file, signedId: null, uploading: true }
    this.pendingFiles.push(fileData)
    this.renderFilePreview(fileData)
    this.uploadFile(fileData)
  }

  matchMimeType(type) {
    const accepted = this.acceptedFilesValue.split(',').map(s => s.trim())
    return accepted.some(pattern => {
      if (pattern.endsWith('/*')) {
        return type.startsWith(pattern.replace('/*', '/'))
      }
      return pattern === type
    })
  }

  renderFilePreview(fileData) {
    if (!this.hasFilePreviewTarget) return

    const preview = document.createElement('div')
    preview.id = fileData.id
    preview.className = 'sandbox-file-preview-item'
    preview.innerHTML = `
      <div class="sandbox-file-preview-content">
        ${this.getFilePreviewContent(fileData.file)}
        <div class="sandbox-file-preview-progress" data-progress></div>
      </div>
      <button type="button" class="sandbox-file-remove" data-action="click->sandbox-chat#removeFile" data-file-id="${fileData.id}">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-3 h-3">
          <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
        </svg>
      </button>
    `
    this.filePreviewTarget.appendChild(preview)
    this.updateFilePreviewVisibility()
  }

  getFilePreviewContent(file) {
    if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file)
      return `<img src="${url}" alt="${file.name}" class="sandbox-file-preview-image" />`
    }
    const ext = file.name.split('.').pop().toUpperCase()
    return `<div class="sandbox-file-preview-icon">${ext}</div>`
  }

  uploadFile(fileData) {
    new Upload(
      fileData.file,
      this.directUploadUrlValue,
      (signedId, error) => {
        if (error) {
          this.removeFileById(fileData.id)
          return
        }
        fileData.signedId = signedId
        fileData.uploading = false
        this.updateFileProgress(fileData.id, 100)
      },
      (progress) => {
        this.updateFileProgress(fileData.id, progress)
      }
    )
  }

  updateFileProgress(fileId, progress) {
    const preview = document.getElementById(fileId)
    if (!preview) return

    const progressBar = preview.querySelector('[data-progress]')
    if (progressBar) {
      progressBar.style.width = `${100 - progress}%`
      if (progress >= 100) {
        progressBar.remove()
      }
    }
  }

  removeFile(event) {
    const fileId = event.currentTarget.dataset.fileId
    this.removeFileById(fileId)
  }

  removeFileById(fileId) {
    this.pendingFiles = this.pendingFiles.filter(f => f.id !== fileId)
    const preview = document.getElementById(fileId)
    if (preview) {
      preview.remove()
    }
    this.updateFilePreviewVisibility()
  }

  updateFilePreviewVisibility() {
    if (!this.hasFilePreviewTarget) return
    this.filePreviewTarget.classList.toggle('has-files', this.pendingFiles.length > 0)
  }

  clearPendingFiles() {
    this.pendingFiles = []
    if (this.hasFilePreviewTarget) {
      this.filePreviewTarget.innerHTML = ''
      this.updateFilePreviewVisibility()
    }
  }

  getUploadedFileIds() {
    return this.pendingFiles
      .filter(f => f.signedId && !f.uploading)
      .map(f => f.signedId)
  }

  hasFilesUploading() {
    return this.pendingFiles.some(f => f.uploading)
  }

}
